import React, { useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Permission, usePermission } from "@/shared/hooks/usePermission";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";

export type TabItem = {
  id: string;
  name: string;
  permission?: Permission;
};

export type TabProps = {
  tabs: TabItem[];
  handleChange: (id: string) => void;
  current: string;
};

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

const TabsUI: React.FC<TabProps> = ({ tabs, handleChange, current }) => {
  const { can } = usePermission();
  const tabsContainerRef = useRef<HTMLDivElement>(null);

  const handleTabChange = (id: string) => {
    handleChange(id);
  };

  const scrollLeft = () => {
    if (tabsContainerRef.current) {
      tabsContainerRef.current.scrollBy({
        left: -300,
        behavior: "smooth",
      });
    }
  };

  const scrollRight = () => {
    if (tabsContainerRef.current) {
      tabsContainerRef.current.scrollBy({
        left: 300,
        behavior: "smooth",
      });
    }
  };

  // Define el número máximo de tabs antes de que se necesite el scroll
  const maxTabsBeforeScroll = 5;

  return (
    <div className="relative">
      {tabs.length > maxTabsBeforeScroll && (
        <button
          onClick={scrollLeft}
          className="absolute left-0 px-4 ml-1 top-1/2 -translate-y-1/2 bg-gradient-to-l from-gray-100 to-gray-300 text-gray-600 rounded-full p-2 shadow-md hover:from-gray-300 hover:to-gray-400 focus:outline-none"
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>
      )}

      {/* Contenedor de tabs */}
      <div
        ref={tabsContainerRef}
        className={`flex items-center overflow-x-auto scrollbar-hide space-x-4 ${tabs.length > maxTabsBeforeScroll ? "ml-12 mr-12" : ""}`}
      >
        {tabs.map(
          (tab) =>
            tab.permission &&
            can(tab.permission) && (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={classNames(
                  current === tab.id
                    ? "border-primary-500 text-primary-600"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700",
                  "flex-shrink-0 whitespace-nowrap border-b-2 px-5 py-3 text-sm font-medium"
                )}
              >
                {tab.name}
              </button>
            )
        )}
      </div>

      {tabs.length > maxTabsBeforeScroll && (
        <button
          onClick={scrollRight}
          className="absolute px-4 mr-1 right-0 top-1/2 -translate-y-1/2 bg-gradient-to-r from-gray-100 to-gray-300 bg-gray-200 text-gray-600 rounded-full p-2 shadow-md hover:bg-gray-300 focus:outline-none"
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </button>
      )}
    </div>
  );
};

export default TabsUI;
